import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { Controller, useForm } from "react-hook-form";

import EditOrder from "./EditOrder";
import { useGetAllTechniciansQuery } from "../../services/technician/technicianServices";
import { updateOrder } from "../../features/order/orderActions";
import { useDispatch, useSelector } from "react-redux";
import OrderDetailsDialog from "./OrderDetailsDialog";

// Modified to accept orders data, loading state, error, and refetch function as props
const OrderTableByTechnician = ({
  ordersData = null,
  isLoadingOrders = false,
  ordersError = null,
  refetchOrders = () => {},
}) => {
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [editOrderId, setEditOrderId] = useState(null);
  const [editOrderVisible, setEditOrderVisible] = useState(false);
  const [visible, setVisible] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [filteredData, setFilteredData] = useState([]); // Add this state to track filtered data from all filters
  const navigate = useNavigate();

  const { updateLoading: assignTechnicianState } = useSelector(
    (state) => state.orders
  );

  const dispatch = useDispatch();

  const toast = useRef(null);

  const { data: techniciansData, isLoading: techniciansLoading } =
    useGetAllTechniciansQuery("TechniciansDetails", {
      refetchOnMountOrArgChange: true,
    });

  const defaultValues = {
    technicianId: "",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({ defaultValues });

  const onSubmit = (data) => {
    const obj = { technician_id: data.technicianId.code, id: orderId };
    dispatch(updateOrder(obj)).then((res) => {
      if (res?.payload?.data?.status === "Success") {
        refetchOrders(); // Use the passed refetch function
        setVisible(false);
        reset();
        setOrderId("");
        toast.current.show({
          severity: "success",
          summary: "Form Submitted",
          detail: res.payload.data.message,
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: res.payload,
          life: 3000,
        });
      }
    });
  };

  const getFormErrorMessage = (name) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    );
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
  };

  const header = () => {
    return (
      <div className="flex items-center justify-between bg-[#6E9889] mb-2">
        <div className="flex gap-2">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="p-inputtext-sm rounded-xl"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Filter by Customer Name"
            />
          </span>

          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="p-inputtext-sm rounded-xl"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              placeholder="Filter by Product"
            />
          </span>
        </div>
        <div>
          <Button
            icon="pi pi-file-pdf"
            className="bg-[#F40F02] border-[#F40F02]"
            onClick={exportPdf}
            disabled={filteredData.length === 0 || ordersData?.data?.length === 0}
            rounded
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    const filtered = getFilteredData();
    setFilteredData(filtered);
  }, [productFilter, ordersData]);


  const getFilteredData = () => {
    if (!ordersData?.data) return [];
    if (!productFilter.trim()) return ordersData.data;

    return ordersData.data.filter((row) => {
      if (!row.order_line_items || !row.order_line_items.length) return false;

      const filterLower = productFilter.toLowerCase().trim();

      return row.order_line_items.some((item) => {
        if (!item || typeof item.product_name !== "string") return false;
        return item.product_name.toLowerCase().includes(filterLower);
      });
    });
  };

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default({ orientation: "landscape" });

        // Get technician name from the first order (assuming all orders belong to the same technician)
        let technicianName = "N/A";
        if (filteredData.length > 0 && filteredData[0].technician) {
          const { first_name, last_name } = filteredData[0].technician;
          technicianName = `${first_name || ""} ${last_name || ""}`.trim();
        }

        // Add technician name at the top left
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text(`Technician: ${technicianName}`, 10, 10);

        // Add a small space after the technician name
        const exportColumns = [
          { header: "Sequence", dataKey: "order_road_sequence", width: 15 },
          { header: "Customer Name", dataKey: "customer.full_name", width: 25 },
          {
            header: "Delivery Date",
            dataKey: "order_delivery.estimated_delivery_date",
            width: 20,
          },
          { header: "Telephone", dataKey: "customer.phone", width: 20 },
          { header: "Address", dataKey: "customer.full_address", width: 35 },
          { header: "Payment Status", dataKey: "payment_status", width: 20 },
          { header: "Products", dataKey: "products", width: 45 },
          {
            header: "Delivery Notes",
            dataKey: "order_delivery.delivery_notes",
            width: 30,
          },
        ];

        // Use the filtered data captured by onValueChange
        const data = filteredData.map((order) => {
          // Format products
          let products = "";
          if (order.order_line_items && order.order_line_items.length) {
            products = order.order_line_items
              .map(
                (item) =>
                  `${item.product_name}${
                    item.line_item_quantity > 1
                      ? ` (${item.line_item_quantity}).`
                      : ""
                  }`
              )
              .join("\n");
          } else {
            products = "No products";
          }

          return {
            order_road_sequence: order.order_road_sequence || "--",
            "customer.full_name": order.customer?.full_name || "--",
            "order_delivery.estimated_delivery_date":
              order.order_delivery?.estimated_delivery_date || "--",
            "customer.phone": order.customer?.phone || "--",
            "customer.full_address": order.customer?.full_address || "--",
            "order_delivery.delivery_notes":
              order.order_delivery?.delivery_notes || "--",
            payment_status: order.payment_status || order.status || "--",
            products: products,
          };
        });

        doc.autoTable({
          head: [exportColumns.map((col) => col.header)],
          body: data.map((row) =>
            exportColumns.map((col) => row[col.dataKey] || "--")
          ),
          columnStyles: {
            0: { cellWidth: 15 }, // Sequence Number
            1: { cellWidth: 25 }, // Customer Name
            2: { cellWidth: 20 }, // Delivery Date
            3: { cellWidth: 20 }, // Telephone
            4: { cellWidth: 35 }, // Address
            5: { cellWidth: 20 }, // Payment Status
            6: { cellWidth: 45 }, // Products
            7: { cellWidth: 30 }, // Delivery Notes
          },
          styles: {
            overflow: "linebreak",
            cellWidth: "wrap",
            fontSize: 8,
            cellPadding: 1,
            valign: "middle",
          },
          margin: { top: 20, left: 5, right: 5 }, // Increased top margin to make room for the technician name
          didParseCell: function (data) {
            if (
              data.column.dataKey === "products" ||
              data.column.dataKey === "order_delivery.delivery_notes"
            ) {
              data.cell.styles.cellWidth = "wrap";
              data.cell.styles.valign = "top";
            }
          },
        });

        doc.save("technician_orders.pdf");
      });
    });
  };

  const onEditOrder = (id) => {
    setEditOrderId(id);
    setEditOrderVisible(true);
  };

  const onShow = (id) => {
    setVisible(true);
    setOrderId(id);
  };

  const actionBodyTemplate = (rowData) => {
    const { id } = rowData;
    return (
      <div className="flex items-center justify-center">
        <Button
          icon="pi pi-pencil"
          size="small"
          text
          rounded
          onClick={() => onEditOrder(id)}
        />
        <Button
          label="Create Receipt"
          icon="pi pi-print"
          size="small"
          text
          rounded
          onClick={() => {
            navigate(`/orders/${id}`);
          }}
        />
        <Button
          icon="pi pi-user-edit"
          size="small"
          text
          label="Assign Technician"
          rounded
          onClick={() => onShow(id)}
        />
      </div>
    );
  };

  const onShowDetails = (id) => {
    setOrderId(id);
    setOrderDetailsVisible(true);
  };

  const customerNameBodyTemplate = (rowData) => {
    const { customer, order_date } = rowData;
    return (
      <div className="m-4 text-sm font-bold">
        {customer.full_name} - {order_date}
      </div>
    );
  };

  const paymentStatusBodyTemplate = (rowData) => {
    const { payment_status: paymentStatus, status: paymentStatusExist } =
      rowData;
    return (
      <div className="m-4 text-sm font-bold">
        {paymentStatus ? paymentStatus : paymentStatusExist}
      </div>
    );
  };

  const modeOfPaymentBodyTemplate = (rowData) => {
    const { mode_of_payment } = rowData;
    return <div className="m-4 text-sm font-bold">{mode_of_payment}</div>;
  };

  const deliveryStatusBodyTemplate = (rowData) => {
    const { order_delivery } = rowData;
    return (
      <div className="m-4 text-sm font-bold">
        {order_delivery?.delivery_status}
      </div>
    );
  };

  const productsNameBodyTemplate = (rowData) => {
    const { order_line_items } = rowData;

    if (!order_line_items || !order_line_items.length) {
      return <div className="m-4 text-sm font-bold">No products</div>;
    }

    return (
      <div className="m-4 text-sm font-bold">
        {order_line_items.map((item, index) => (
          <div key={item.id} className={index > 0 ? "mt-1" : ""}>
            {item.product_name}
            {item.line_item_quantity > 1 ? ` (${item.line_item_quantity})` : ""}
            .
          </div>
        ))}
      </div>
    );
  };

  const isDesktop = useMediaQuery({ minWidth: 992 });
  const isMobile = useMediaQuery({ maxWidth: 767 });

  if (ordersError) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <p>{ordersError?.data?.message || "An error occurred"}</p>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Assign a technician"
        visible={visible}
        style={
          isDesktop ? { width: "35vw" } : isMobile ? { width: "90vw" } : {}
        }
        onHide={() => setVisible(false)}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="my-8">
          <Controller
            name="technicianId"
            control={control}
            rules={{ required: "Technician is required." }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <label
                  htmlFor={field.name}
                  className={classNames({ "p-error": errors.value })}
                >
                  Select a technician:
                </label>
                <span>
                  <Dropdown
                    id={field.name}
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={
                      techniciansData
                        ? techniciansData.data.map((technician) => ({
                            name: technician.full_name,
                            code: technician.id,
                          }))
                        : []
                    }
                    loading={techniciansLoading}
                    optionLabel="name"
                    className={`${classNames({
                      "p-invalid": fieldState.error,
                    })} w-full rounded-xl`}
                    placeholder="Select a Technician"
                  />
                </span>
                {getFormErrorMessage(field.name)}
              </div>
            )}
          />
          <div className="flex items-center justify-center">
            <Button
              label={assignTechnicianState ? "" : "Submit"}
              className="bg-none text-[#0D6047] border-none rounded-xl"
              type="submit"
              icon={
                assignTechnicianState ? "pi pi-spin pi-spinner" : "pi pi-check"
              }
            />
          </div>
        </form>
      </Dialog>
      {editOrderId && (
        <EditOrder
          editOrderId={editOrderId}
          editOrderVisible={editOrderVisible}
          refetch={refetchOrders}
          toast={toast}
          setEditOrderVisible={setEditOrderVisible}
        />
      )}
      {orderId && (
        <OrderDetailsDialog
          visible={orderDetailsVisible}
          setVisible={setOrderDetailsVisible}
          setOrderId={setOrderId}
          id={orderId}
        />
      )}
      <DataTable
        value={getFilteredData()}
        dataKey="order_road_sequence"
        scrollable
        scrollHeight="68vh"
        loading={isLoadingOrders}
        header={header}
        globalFilter={globalFilterValue}
        paginator
        rows={50}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onRowClick={(e) => {
          setOrderId(null);
          onShowDetails(e?.data?.id);
        }}
        rowClassName="cursor-pointer"
        sortField="order_road_sequence"
        sortOrder={1}
        onValueChange={(filteredValues) => setFilteredData(filteredValues)}
      >
        <Column header="Sequence" field="order_road_sequence" />
        <Column header="Customer Name" field="customer.full_name" />
        <Column
          header="Schedule delivery date"
          filter
          filterMatchMode="contains"
          field="order_delivery.estimated_delivery_date"
        />
        <Column
          header="Telephone"
          field="customer.phone"
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Address"
          field="customer.full_address"
          style={{ minWidth: "20rem" }}
        />
        <Column
          field="payment_status"
          header="Payment Status"
          body={paymentStatusBodyTemplate}
          style={{ minWidth: "12rem" }}
        />
        {/* <Column
          field="mode_of_payment"
          header="Mode of Payment"
          body={modeOfPaymentBodyTemplate}
        />
        <Column
          style={{ minWidth: "15rem" }}
          field="order_delivery.delivery_status"
          header="Delivery Status"
          filter
          body={deliveryStatusBodyTemplate}
        /> */}
        <Column
          field="order_line_items"
          header="Products"
          body={productsNameBodyTemplate}
          style={{ minWidth: "16rem" }}
        />{" "}
        <Column
          header="Delivery Notes"
          field="order_delivery.delivery_notes"
          style={{ minWidth: "12rem" }}
        />
        {/* <Column header="Actions" body={actionBodyTemplate} exportable={false} /> */}
      </DataTable>
    </>
  );
};

export default OrderTableByTechnician;
